import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  DoorOpen, 
  TreePine, 
  Plus, 
  Settings,
  Trash2,
  Home,
  Building2,
  MapPin,
  Eye,
  Warehouse,
  Car,
  Utensils,
  Bed,
  Edit3,
  Link
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Available icons for overlay buttons
const OVERLAY_ICONS = {
  door: DoorOpen,
  outdoor: TreePine,
  home: Home,
  building: Building2,
  pin: MapPin,
  eye: Eye,
  warehouse: Warehouse,
  car: Car,
  kitchen: Utensils,
  bedroom: Bed,
};

// Predefined button styles
const BUTTON_STYLES = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  glass: 'glass text-foreground hover:bg-secondary/50',
  outline: 'border-2 border-primary bg-background/80 text-primary hover:bg-primary/10',
};

export function ViewerOverlay({ 
  overlayButtons = [],
  onNavigate,
  onAddButton,
  onRemoveButton,
  onUpdateButton,
  sweeps = [],
  currentSweep = null,
  cameraPosition = null,
  readOnly = false,
  hideSettings = false,
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [newButton, setNewButton] = useState({
    label: '',
    icon: 'door',
    sweepId: '',
    rotation: null, // Store camera rotation too
    position: { x: 50, y: 85 },
    style: 'primary',
  });

  // Disable edit mode if readOnly
  const canEdit = !readOnly && onAddButton && onRemoveButton && onUpdateButton;

  const handleButtonClick = (button) => {
    if (isEditMode && canEdit) {
      // In edit mode, open edit dialog
      setEditingButton({ ...button });
      return;
    }
    
    // Handle both camelCase (frontend) and snake_case (database) field names
    const sweepId = button.sweepId || button.sweep_id;
    
    if (sweepId && onNavigate) {
      // Use specific sweep ID if set, with optional rotation
      onNavigate(sweepId, { rotation: button.rotation });
    } else if (sweeps.length > 0 && onNavigate) {
      // Use predefined indexes:
      // Inside = Scan 16 (index 15)
      // Outside = Scan 1 (index 0)
      let sweepIndex = 0;
      
      if (button.label.toLowerCase().includes('inside')) {
        sweepIndex = 15; // Scan 16
      } else if (button.label.toLowerCase().includes('outside')) {
        sweepIndex = 0; // Scan 1
      }
      
      // Make sure index is within bounds
      sweepIndex = Math.min(sweepIndex, sweeps.length - 1);
      
      console.log(`Navigating to Scan ${sweepIndex + 1}:`, sweeps[sweepIndex]?.id);
      onNavigate(sweeps[sweepIndex]?.id);
    } else {
      // Fallback: Show message that SDK needs to connect
      console.log('SDK not fully connected - sweeps not available');
      alert(`To use "${button.label}" navigation:\n\n1. Wait for Matterport domain approval, OR\n2. Run locally on localhost:443\n\nThe SDK needs to be connected to get sweep positions.`);
    }
  };

  // Save current camera rotation along with sweep
  const handleUseCurrentPosition = () => {
    if (currentSweep?.id && editingButton) {
      setEditingButton(prev => ({ 
        ...prev, 
        sweepId: currentSweep.id,
        rotation: cameraPosition?.rotation || null,
      }));
    }
  };

  const handleUseCurrentPositionForNew = () => {
    if (currentSweep?.id) {
      setNewButton(prev => ({ 
        ...prev, 
        sweepId: currentSweep.id,
        rotation: cameraPosition?.rotation || null,
      }));
    }
  };

  const handleAddButton = () => {
    if (newButton.label && onAddButton) {
      onAddButton({
        ...newButton,
        id: `overlay-${Date.now()}`,
      });
      setNewButton({
        label: '',
        icon: 'door',
        sweepId: '',
        rotation: null,
        position: { x: 50, y: 85 },
        style: 'primary',
      });
      setIsAddDialogOpen(false);
    }
  };

  const handleUpdateButton = () => {
    if (editingButton && onUpdateButton) {
      onUpdateButton(editingButton.id, editingButton);
      setEditingButton(null);
    }
  };

  const IconComponent = (iconName) => {
    return OVERLAY_ICONS[iconName] || OVERLAY_ICONS.pin;
  };

  return (
    <>
      {/* Centered Button Container - positioned above Matterport controls */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 flex items-center gap-4">
        {overlayButtons.map((button) => {
          const Icon = IconComponent(button.icon);
          
          return (
            <div
              key={button.id}
              className={cn(
                "transition-all duration-300",
                isEditMode && "ring-2 ring-primary ring-offset-2 ring-offset-background rounded-xl cursor-pointer"
              )}
            >
            <div className="relative group">
              <Button
                className={cn(
                  "min-w-[100px] h-12 gap-2 rounded-xl font-semibold shadow-lg transition-all",
                  BUTTON_STYLES[button.style] || BUTTON_STYLES.primary,
                  "hover:scale-105 active:scale-95",
                  isEditMode && "animate-pulse"
                )}
                onClick={() => handleButtonClick(button)}
              >
                <Icon className="h-5 w-5" />
                <span>{button.label}</span>
              </Button>
              
              {/* Sweep ID indicator */}
              {button.sweepId && (
                <Badge 
                  variant="secondary" 
                  className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] px-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Link className="h-2 w-2 mr-1" />
                  {button.sweepId.substring(0, 8)}...
                </Badge>
              )}
              
              {/* Edit indicator in edit mode */}
              {isEditMode && (
                <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full p-1">
                  <Edit3 className="h-3 w-3" />
                </div>
              )}
            </div>
          </div>
        );
      })}
      </div>

      {/* Edit Button Dialog */}
      <Dialog open={!!editingButton} onOpenChange={(open) => !open && setEditingButton(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Button: {editingButton?.label}</DialogTitle>
            <DialogDescription>
              Update this button settings or link it to a specific scan position.
            </DialogDescription>
          </DialogHeader>
          
          {editingButton && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-label">Button Label</Label>
                <Input
                  id="edit-label"
                  value={editingButton.label}
                  onChange={(e) => setEditingButton(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(OVERLAY_ICONS).map(([key, Icon]) => (
                    <Button
                      key={key}
                      variant={editingButton.icon === key ? 'default' : 'outline'}
                      size="icon"
                      onClick={() => setEditingButton(prev => ({ ...prev, icon: key }))}
                    >
                      <Icon className="h-4 w-4" />
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Button Style</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(BUTTON_STYLES).map((key) => (
                    <Button
                      key={key}
                      variant={editingButton.style === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditingButton(prev => ({ ...prev, style: key }))}
                      className="capitalize"
                    >
                      {key}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-sweep">Sweep ID (Scan Position)</Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-sweep"
                    placeholder="Enter sweep ID or use current position"
                    value={editingButton.sweepId}
                    onChange={(e) => setEditingButton(prev => ({ ...prev, sweepId: e.target.value }))}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUseCurrentPosition}
                    disabled={!currentSweep?.id}
                    title="Use current scan position"
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    Current
                  </Button>
                </div>
                {currentSweep?.id && (
                  <p className="text-xs text-muted-foreground">
                    Current position: <code className="bg-muted px-1 rounded">{currentSweep.id}</code>
                  </p>
                )}
                {editingButton?.rotation && (
                  <p className="text-xs text-green-500">
                    ✓ Camera rotation will be saved with this button
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  💡 Navigate AND rotate to the exact view you want, then click Current to capture position + rotation.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Position on Screen</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Horizontal %</Label>
                    <Input
                      type="number"
                      min="5"
                      max="95"
                      value={editingButton.position.x}
                      onChange={(e) => setEditingButton(prev => ({
                        ...prev,
                        position: { ...prev.position, x: parseInt(e.target.value) || 50 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Vertical %</Label>
                    <Input
                      type="number"
                      min="5"
                      max="95"
                      value={editingButton.position.y}
                      onChange={(e) => setEditingButton(prev => ({
                        ...prev,
                        position: { ...prev.position, y: parseInt(e.target.value) || 85 }
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => {
                if (onRemoveButton && editingButton) {
                  onRemoveButton(editingButton.id);
                  setEditingButton(null);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingButton(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateButton}>
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings & Add Button - Above logo overlay - Only show for editors, hidden when hideSettings is true */}
      {canEdit && !hideSettings && (
      <div className="absolute bottom-20 right-4 z-20 flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="glass"
              size="icon"
              className="rounded-xl"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Overlay Buttons</span>
                <Badge variant="secondary" className="text-[10px]">
                  {overlayButtons.length}
                </Badge>
              </div>

              {/* Current Sweep ID Display */}
              {currentSweep?.id && (
                <div className="p-2 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Current Scan Position:</p>
                  <code className="text-xs font-mono bg-background px-2 py-1 rounded block truncate">
                    {currentSweep.id}
                  </code>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Edit Mode</span>
                <Button
                  variant={isEditMode ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setIsEditMode(!isEditMode)}
                >
                  {isEditMode ? 'Done' : 'Edit'}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                {isEditMode 
                  ? "Click any button to edit its settings and link to a scan position." 
                  : "Enable Edit Mode to configure buttons."}
              </p>
              
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    <Plus className="h-3 w-3" />
                    Add New Button
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add Overlay Button</DialogTitle>
                    <DialogDescription>
                      Create a navigation button that floats on the 3D viewer.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="btn-label">Button Label *</Label>
                      <Input
                        id="btn-label"
                        placeholder="e.g., Tank Interior, Control Room..."
                        value={newButton.label}
                        onChange={(e) => setNewButton(prev => ({ ...prev, label: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Icon</Label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(OVERLAY_ICONS).map(([key, Icon]) => (
                          <Button
                            key={key}
                            variant={newButton.icon === key ? 'default' : 'outline'}
                            size="icon"
                            onClick={() => setNewButton(prev => ({ ...prev, icon: key }))}
                          >
                            <Icon className="h-4 w-4" />
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Button Style</Label>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(BUTTON_STYLES).map((key) => (
                          <Button
                            key={key}
                            variant={newButton.style === key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setNewButton(prev => ({ ...prev, style: key }))}
                            className="capitalize"
                          >
                            {key}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="btn-sweep">Sweep ID (Scan Position)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="btn-sweep"
                          placeholder="Navigate to position, then click Current"
                          value={newButton.sweepId}
                          onChange={(e) => setNewButton(prev => ({ ...prev, sweepId: e.target.value }))}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleUseCurrentPositionForNew}
                          disabled={!currentSweep?.id}
                        >
                          <MapPin className="h-4 w-4 mr-1" />
                          Current
                        </Button>
                      </div>
                      {currentSweep?.id && (
                        <p className="text-xs text-muted-foreground">
                          Current: <code className="bg-muted px-1 rounded">{currentSweep.id}</code>
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Position on Screen</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Horizontal %</Label>
                          <Input
                            type="number"
                            min="5"
                            max="95"
                            value={newButton.position.x}
                            onChange={(e) => setNewButton(prev => ({
                              ...prev,
                              position: { ...prev.position, x: parseInt(e.target.value) || 50 }
                            }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Vertical %</Label>
                          <Input
                            type="number"
                            min="5"
                            max="95"
                            value={newButton.position.y}
                            onChange={(e) => setNewButton(prev => ({
                              ...prev,
                              position: { ...prev.position, y: parseInt(e.target.value) || 85 }
                            }))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddButton} disabled={!newButton.label}>
                      Add Button
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      )}

      {/* Edit Mode Indicator - Only show for editors */}
      {canEdit && isEditMode && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
          <Badge variant="default" className="bg-primary text-primary-foreground px-4 py-1.5 text-sm shadow-lg">
            Edit Mode — Click a button to configure it
          </Badge>
        </div>
      )}
    </>
  );
}

export default ViewerOverlay;
